import GlassCard from '@/components/GlassCard';
import useInertiaData from '@/hooks/useInertiaData';
import { useThemeColors } from '@/hooks/useThemeColors';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface Course {
    id: number;
    title: string;
    slug: string;
    description: string | null;
    image: string | null;
    professor?: {
        id: number;
        full_name: string;
        email: string;
    } | null;
    category: {
        id: number;
        name: string;
        master?: boolean;
    };
    total_topics: number;
    total_classes: number;
    completed_classes: number;
    progress_percentage: number;
    last_activity?: string;
    enrollment?: {
        created_at: string;
        expires_at: string | null;
    };
}

interface CourseCardProps {
    course: Course;
    onClick?: () => void;
}

const CourseCard: React.FC<CourseCardProps> = ({ course, onClick }) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const { user, account_paused } = useInertiaData();
    const isEnrolled = !account_paused && (Boolean(course.enrollment) || Boolean(user?.is_superuser));

    return (
        <GlassCard
            onClick={onClick}
            className={`group relative overflow-hidden shadow-lg transition-all duration-300 ${
                !isEnrolled
                    ? 'grayscale opacity-60 cursor-not-allowed'
                    : 'cursor-pointer hover:border-[var(--color-theme-primary)] hover:shadow-xl'
            }`}
        >
            {/* Course Image */}
            <div className="relative overflow-hidden bg-gray-200 dark:bg-neutral-700">
                {course.image ? (
                    <img
                        src={course.image}
                        alt={course.title}
                        className="w-full h-auto transition-transform duration-300 group-hover:scale-110"
                    />
                ) : (
                    <div
                        className={`flex aspect-video items-center justify-center bg-gradient-to-br from-${colors.primary} to-${colors.accent}`}
                    >
                        <svg
                            className="h-16 w-16 text-white opacity-50"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                            />
                        </svg>
                    </div>
                )}

                {/* Topics/Classes count badge */}
                <div className="bg-opacity-60 absolute top-2 left-2 rounded bg-black px-2 py-1 text-xs text-white">
                    {course.total_topics > 0 ? (
                        <span>
                            {course.total_topics} {t('home.topics')}
                        </span>
                    ) : (
                        <span>
                            {course.total_classes} {t('course.classes_other')}
                        </span>
                    )}
                </div>
            </div>

            {/* Course Content */}
            <div className="p-4">
                <h3 className="mb-2 line-clamp-2 text-lg font-semibold text-gray-900 dark:text-white">
                    {course.title}
                </h3>

                {isEnrolled ? (
                    <>
                        {/* Progress Section */}
                        <div className="mb-3">
                            <div className="mb-1 flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                                    {course.progress_percentage}
                                    {t('home.percentCompleted')}
                                </span>
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-neutral-700">
                                <div
                                    className="h-full rounded-full transition-all duration-300"
                                    style={{
                                        width: `${course.progress_percentage}%`,
                                        backgroundColor: colors.progressBar,
                                    }}
                                />
                            </div>
                        </div>

                        {/* Last Activity */}
                        {course.last_activity && (
                            <p className="text-xs text-gray-500 dark:text-neutral-400">
                                {t('home.lastActivity')} {course.last_activity}
                            </p>
                        )}
                    </>
                ) : (
                    <p className="text-xs text-gray-400 dark:text-neutral-500 italic">
                        {t('home.noAccessToCourse')}
                    </p>
                )}
            </div>
        </GlassCard>
    );
};

export default CourseCard;
